import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Database, Table, Loader2, Brain, Play, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { apiRequest } from "@/lib/queryClient";

interface TableSchema {
  tableName: string;
  fields: Array<{
    name: string;
    type: string;
    mode: string;
    description?: string;
  }>;
  rowCount?: number;
  sampleData?: any[];
}

interface DatasetAnalysis {
  datasetId: string;
  tables: TableSchema[];
  rawSchemaText: string;
}

export default function SchemaPage() {
  const [selectedDataset, setSelectedDataset] = useState<string>("analytics_318091150");
  const [aiAnalysis, setAiAnalysis] = useState<string>("");
  const [customQuery, setCustomQuery] = useState<string>("");
  const [queryResult, setQueryResult] = useState<any[] | null>(null);
  const [queryError, setQueryError] = useState<string>("");

  const { data: datasetsData, isLoading: loadingDatasets } = useQuery<{ datasets: string[] }>({
    queryKey: ["/api/schema/datasets"],
  });

  const {
    data: schemaData,
    isLoading: loadingSchema,
    error: schemaError,
    refetch: refetchSchema
  } = useQuery<DatasetAnalysis>({
    queryKey: ["/api/schema", selectedDataset],
    queryFn: async () => {
      const res = await fetch(`/api/schema/${selectedDataset}`);
      if (!res.ok) throw new Error("Failed to load schema");
      return res.json();
    },
    enabled: !!selectedDataset,
  });

  const analyzeMutation = useMutation({
    mutationFn: async (schemaText: string) => {
      const res = await apiRequest("POST", "/api/schema/analyze", { schemaText });
      return res.json();
    },
    onSuccess: (data) => {
      setAiAnalysis(data.analysis);
    },
  });

  const queryMutation = useMutation({
    mutationFn: async (query: string) => {
      const res = await apiRequest("POST", "/api/query", { query });
      return res.json();
    },
    onSuccess: (data) => {
      setQueryResult(data.data);
      setQueryError("");
    },
    onError: (error: any) => {
      setQueryError(error.message || "Query failed");
      setQueryResult(null);
    },
  });

  const handleAnalyze = () => {
    if (schemaData?.rawSchemaText) {
      analyzeMutation.mutate(schemaData.rawSchemaText);
    }
  };

  const handleRunQuery = () => {
    if (customQuery.trim()) {
      queryMutation.mutate(customQuery);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Database className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-schema-title">
            Schema Explorer
          </h1>
        </div>
        <p className="text-muted-foreground">
          Explore and analyze your BigQuery datasets with AI assistance
        </p>
      </div>

      <div className="flex items-center gap-4">
        <Select value={selectedDataset} onValueChange={setSelectedDataset}>
          <SelectTrigger className="w-[300px]" data-testid="select-dataset">
            <SelectValue placeholder="Select a dataset" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="analytics_318091150">analytics_318091150 (Marketing)</SelectItem>
            <SelectItem value="commusoft_sales_data">commusoft_sales_data (Sales)</SelectItem>
            {datasetsData?.datasets
              ?.filter(d => !["analytics_318091150", "commusoft_sales_data"].includes(d))
              .map(d => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
          </SelectContent>
        </Select>

        <Button
          onClick={() => refetchSchema()}
          disabled={loadingSchema}
          variant="outline"
          data-testid="button-refresh-schema"
        >
          {loadingSchema ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load Schema"}
        </Button>

        <Button
          onClick={handleAnalyze}
          disabled={!schemaData || analyzeMutation.isPending}
          data-testid="button-analyze-schema"
        >
          <Brain className="h-4 w-4 mr-2" />
          {analyzeMutation.isPending ? "Analyzing..." : "Analyze with AI"}
        </Button>
      </div>

      {schemaError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load schema. Please check your BigQuery credentials.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Table className="h-5 w-5" />
              Tables in {selectedDataset}
            </CardTitle>
            <CardDescription>
              {schemaData ? `${schemaData.tables.length} tables found` : "Loading..."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingSchema ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : schemaData?.tables.length === 0 ? (
              <p className="text-muted-foreground">No tables found in this dataset.</p>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {schemaData?.tables.map((table, idx) => (
                  <AccordionItem key={table.tableName} value={table.tableName}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{table.tableName}</span>
                        <Badge variant="secondary" className="text-xs">
                          {table.rowCount?.toLocaleString() || "?"} rows
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 pl-2">
                        {table.fields.map((field) => (
                          <div key={field.name} className="flex items-center gap-2 text-sm">
                            <span className="font-mono text-primary">{field.name}</span>
                            <Badge variant="outline" className="text-xs">{field.type}</Badge>
                            {field.mode === "REQUIRED" && (
                              <Badge variant="secondary" className="text-xs">required</Badge>
                            )}
                          </div>
                        ))}
                        {table.sampleData && table.sampleData.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-muted-foreground mb-2">Sample data:</p>
                            <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
                              {JSON.stringify(table.sampleData[0], null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          {aiAnalysis && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5" />
                  AI Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
                  {aiAnalysis}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Run Custom Query</CardTitle>
              <CardDescription>
                Execute SELECT queries against your BigQuery data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder={`SELECT * FROM \`aquila-479614.${selectedDataset}.table_name\` LIMIT 10`}
                value={customQuery}
                onChange={(e) => setCustomQuery(e.target.value)}
                className="font-mono text-sm min-h-[100px]"
                data-testid="input-query"
              />
              <Button
                onClick={handleRunQuery}
                disabled={!customQuery.trim() || queryMutation.isPending}
                data-testid="button-run-query"
              >
                <Play className="h-4 w-4 mr-2" />
                {queryMutation.isPending ? "Running..." : "Run Query"}
              </Button>

              {queryError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{queryError}</AlertDescription>
                </Alert>
              )}

              {queryResult && (
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground mb-2">
                    {queryResult.length} rows returned
                  </p>
                  <pre className="text-xs bg-muted p-3 rounded overflow-x-auto max-h-[300px]">
                    {JSON.stringify(queryResult, null, 2)}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
